import {
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isAfter,
  isEqual,
  isSameDay,
  max,
  min,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { useMemo } from 'react';
import type { Mode } from '../../../../../types';
import type { ContentEvent } from '../../calendar-layout-content';
import type { EventItem } from '../Agenda';

type DateInterval = {
  end: Date;
  start: Date;
};

type P = {
  date: Date;
  events?: ContentEvent[];
  mode: Mode;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
};

const getInterval = ({ date, mode, weekStartsOn }: Pick<P, 'date' | 'mode' | 'weekStartsOn'>) => {
  switch (mode) {
    case 'day':
      return { end: endOfDay(date), start: startOfDay(date) };
    case 'month':
      return { end: endOfMonth(date), start: startOfMonth(date) };
    case 'week':
      return { end: endOfWeek(date, { weekStartsOn }), start: startOfWeek(date, { weekStartsOn }) };
  }
};

const getAgendaTimeDisplay = ({
  event,
  isFirstEventDay,
  isLastEventDay,
}: {
  event: ContentEvent;
  isFirstEventDay: boolean;
  isLastEventDay: boolean;
}): EventItem['agendaTimeDisplay'] => {
  if ((event.type === 'node' && event.data.isAllDay) || (!isFirstEventDay && !isLastEventDay)) {
    return 'allDay';
  }

  return isFirstEventDay ? 'startTime' : 'endTime';
};

const createEventListItem = ({
  agendaTimeDisplay,
  event,
  id,
  startTime,
}: {
  agendaTimeDisplay?: EventItem['agendaTimeDisplay'];
  event: ContentEvent;
  id: string;
  startTime: Date;
}): EventItem => {
  if (event.type === 'node') {
    return {
      ...event,
      agendaTimeDisplay,
      data: {
        ...event.data,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- filtered before creating items
        categories: [event.data.categories[0]!],
      },
      id,
      startTime,
    };
  }

  return {
    ...event,
    agendaTimeDisplay,
    id,
    startTime,
  };
};

const hasRequiredCategory = (event: ContentEvent) => {
  if (event.type === 'node' && !event.data.categories[0]) {
    console.warn('No category found for event', event.data.id);
    return false;
  }

  return true;
};

const isOutsideInterval = (event: ContentEvent, interval: DateInterval) =>
  event.end.getTime() <= interval.start.getTime() || event.start.getTime() > interval.end.getTime();

const getEffectiveEventEndDay = (event: ContentEvent) => {
  const eventEndDay = startOfDay(event.end);

  return isEqual(event.end, eventEndDay) ? subDays(eventEndDay, 1) : eventEndDay;
};

const createEventListItemsForEvent = ({
  event,
  interval,
}: {
  event: ContentEvent;
  interval: DateInterval;
}): EventItem[] => {
  if (isOutsideInterval(event, interval)) {
    return [];
  }

  const eventIdentifier = event.data.id;
  if (isSameDay(event.start, event.end)) {
    return [createEventListItem({ event, id: eventIdentifier, startTime: event.start })];
  }

  const effectiveEventEndDay = getEffectiveEventEndDay(event);
  const firstVisibleDay = max([startOfDay(event.start), startOfDay(interval.start)]);
  const lastVisibleDay = min([effectiveEventEndDay, startOfDay(interval.end)]);

  if (isAfter(firstVisibleDay, lastVisibleDay)) {
    return [];
  }

  return eachDayOfInterval({ end: lastVisibleDay, start: firstVisibleDay }).map((day) => {
    const agendaTimeDisplay = getAgendaTimeDisplay({
      event,
      isFirstEventDay: isSameDay(day, event.start),
      isLastEventDay: isSameDay(day, effectiveEventEndDay),
    });

    return createEventListItem({
      agendaTimeDisplay,
      event,
      id: `${eventIdentifier}-${day.toISOString()}`,
      startTime: day,
    });
  });
};

export const useCreateEventListItems = ({ date, events, mode, weekStartsOn }: P) =>
  useMemo((): EventItem[] | undefined => {
    if (!events) {
      return undefined;
    }

    const interval = getInterval({ date, mode, weekStartsOn });

    return events
      .filter((event) => hasRequiredCategory(event))
      .flatMap((event) => createEventListItemsForEvent({ event, interval }))
      .toSorted((firstItem, secondItem) => firstItem.startTime.getTime() - secondItem.startTime.getTime());
  }, [date, events, mode, weekStartsOn]);
